
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../hooks/useWeb3';
import { formatAddress } from '../../services/web3';
import MetaMaskButton from '../wallet/MetaMaskButton';
import Button from '../ui/Button';

interface DashboardHeaderProps {
  onConnect?: () => void;
  onDisconnect?: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ onConnect, onDisconnect }) => {
  const navigate = useNavigate();
  const { account, connect, disconnect, loading } = useWeb3();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleConnect = async () => {
    try {
      await connect();
      onConnect?.();
    } catch (error) {
      console.error('Failed to connect:', error);
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    onDisconnect?.();
  };

  return (
    <header className="w-full bg-white/90 backdrop-blur-sm shadow-[0px_4px_32px_rgba(136,136,136,0.1)] rounded-xl xs:rounded-2xl">
      <div className="flex justify-between items-center px-3 xs:px-4 sm:px-6 lg:px-4 py-3 xs:py-4">
        {/* Logo Section */}
        <Link to="/" className="flex items-center gap-1.5 xs:gap-2 sm:gap-3 flex-shrink-0">
          <img 
            src="/images/averpact-logo-full.svg" 
            alt="AverPact Logo" 
            className="h-8 xs:h-9 sm:h-10 w-auto"
          />
        </Link>

        {/* Right Section */}
        <div className="flex items-center gap-1.5 xs:gap-2 sm:gap-3 flex-shrink-0">
          {/* Wallet Connection - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            {account ? (
              <div className="flex items-center gap-3">
                <div className="px-3 py-2 bg-gray-50 rounded-lg flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-[#471b00] font-dm">
                    {formatAddress(account)}
                  </span>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <MetaMaskButton onConnect={handleConnect} />
            )}
          </div>

          {/* Hamburger Menu Icon (Mobile only) */}
          <button 
            className="block lg:hidden p-1.5 xs:p-2" 
            aria-label="Toggle menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <svg className="w-5 h-5 xs:w-6 xs:h-6 text-black-semi" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {menuOpen && (
        <nav className="lg:hidden border-t border-gray-100">
          <div className="flex flex-col py-3 xs:py-4 px-3 xs:px-4 space-y-3">
            {account ? (
              <>
                <div className="px-3 py-2 bg-gray-50 rounded-lg flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-[#471b00] font-dm">
                    {formatAddress(account)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    handleDisconnect();
                    setMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm text-center"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <div className="px-3 py-2">
                <MetaMaskButton onConnect={() => {
                  handleConnect();
                  setMenuOpen(false);
                }} />
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default DashboardHeader;

